import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Textarea } from '../../ui/textarea';
import { AppDispatch } from '../../../redux/store';
import { doGetClaim, doGetClaims, doUpdateClaim } from '../../../modules/claims/stores/thunk';
import { selectClaimById } from '../../../modules/claims/stores/slice';
import moment from 'moment';
import { ArrowUpRight, Loader2, Save, X, CalendarIcon, CircleX, Upload, Image as ImageIcon } from 'lucide-react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { formatClaimStatusText } from './utils';
import { Calendar } from '../../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { cn } from '../../../lib/utils';
import { format } from 'date-fns';
import { useToast } from '../../../hooks/use-toast';
import { convertModelToFormData } from '../../../utils/common';
import { doRemoveClaimImage } from '../../../modules/claims/stores/thunk';

interface ClaimViewProps {
  claimId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ClaimView: React.FC<ClaimViewProps> = ({ claimId, open, onOpenChange }) => {
  const dispatch = useDispatch<AppDispatch>();
  const history = useHistory();
  const { toast } = useToast();

  // Redux state
  const claim = useSelector((state: any) => (claimId ? selectClaimById(state, claimId) : null));
  const isUpdating = useSelector((state: any) => state.claim.action === 'update' && state.claim.isFetching);
  const user = useSelector((state: any) => state.auth.user);
  const userRole = user?.role;

  // Local state for editing
  const [editedReason, setEditedReason] = useState('');
  const [editedStatus, setEditedStatus] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [carrierDecision, setCarrierDecision] = useState<'grant' | 'reject' | null>(null);
  const [completedAtDate, setCompletedAtDate] = useState<Date | null>(null);
  const [completedAtTime, setCompletedAtTime] = useState<string>('');
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Image upload state
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Image modal state
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Add new state
  const [isRemovingImage, setIsRemovingImage] = useState(false);

  // Initialize form when claim changes
  useEffect(() => {
    if (claim) {
      setEditedReason(claim.reason || '');
      setEditedStatus(claim.status);
      setIsEditing(false);
      setCarrierDecision(null);
      setCompletedAtDate(null);
      setCompletedAtTime('');
      setFile(null);
      setPreview(null);
    }
  }, [claim]);

  useEffect(() => {
    if (claimId && !claim) {
      dispatch(doGetClaim(claimId));
    }
  }, [claimId, claim]);

  const canEditClaim = () => {
    // Admin can always edit
    if (userRole === 'ADMIN') {
      return true;
    }

    // Carrier user can only edit when claim status is FILED
    if (userRole === 'CARRIER_USER') {
      return claim?.status === 'FILED' || claim?.status === 'UNDER_REVIEW';
    }

    // Other roles cannot edit
    return false;
  };

  const handleFileSelect = useCallback(
    (selectedFile: File) => {
      const isLt5M = selectedFile.size / 1024 / 1024 <= 5;
      if (!isLt5M) {
        toast({
          title: 'Error',
          description: 'Image must be smaller than 5MB!',
          variant: 'destructive',
        });
        return;
      }

      if (!selectedFile.type.startsWith('image/')) {
        toast({
          title: 'Error',
          description: 'Please select a valid image file.',
          variant: 'destructive',
        });
        return;
      }

      setFile(selectedFile);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(selectedFile);
    },
    [toast],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) {
        handleFileSelect(droppedFile);
      }
    },
    [handleFileSelect],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (selectedFile) {
        handleFileSelect(selectedFile);
      }
    },
    [handleFileSelect],
  );

  const clearFile = useCallback(() => {
    setFile(null);
    setPreview(null);
  }, []);

  // Handle save
  const handleSave = async () => {
    if (!claimId) return;

    // If carrier rejects, set carrierRejected to true, otherwise change status to granted
    let carrierRejected;
    let carrierStatusOverride;
    let orderCompletedAt = null;

    if (carrierDecision === 'reject') {
      carrierRejected = true;
      carrierStatusOverride = 'UNDER_REVIEW';

      // If carrier is rejecting and order doesn't have completedAt, use the specified date/time
      if (!claim.order?.completedAt && completedAtDate && completedAtTime) {
        const [hours, minutes] = completedAtTime.split(':').map(Number);
        const date = new Date(completedAtDate);
        date.setHours(hours, minutes, 0, 0);
        orderCompletedAt = date.toISOString();
      }
    } else if (carrierDecision === 'grant') {
      carrierStatusOverride = 'GRANTED';
    }

    const updateData = {
      id: claimId,
      reason: editedReason,
      status: carrierStatusOverride ? carrierStatusOverride : editedStatus,
      carrierRejected,
      orderCompletedAt,
    };

    try {
      // If there's a file to upload, use FormData
      const formData = convertModelToFormData(updateData);
      if (file) {
        if (file) formData.append('image', file);
        await dispatch(doUpdateClaim(formData));
      } else {
        // Regular JSON update without image
        await dispatch(doUpdateClaim(formData));
      }

      setIsEditing(false);
      onOpenChange(false);
      clearFile();
    } catch (error) {
      console.error('Failed to update claim:', error);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    if (claim) {
      setEditedReason(claim.reason || '');
      setEditedStatus(claim.status);
      setCarrierDecision(null);
      setCompletedAtDate(null);
      setCompletedAtTime('');
      clearFile();
      setIsEditing(false);
    }
  };

  const handleCarrierDecision = (value: 'reject' | 'grant') => {
    setCarrierDecision(value);
    setCompletedAtDate(null);
    setCompletedAtTime(null);
  };

  // Handle image click to open modal
  const handleImageClick = (imageSrc: string) => {
    setSelectedImage(imageSrc);
    setIsImageModalOpen(true);
  };

  // Add new handler
  const handleRemoveImage = async () => {
    if (!claimId) return;

    setIsRemovingImage(true);
    try {
      await dispatch(doRemoveClaimImage(claimId));
      toast({
        title: 'Success',
        description: 'Image removed successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to remove image',
        variant: 'destructive',
      });
    } finally {
      setIsRemovingImage(false);
      dispatch(doGetClaim(claimId));
    }
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'FILED':
        return 'secondary';
      case 'GRANTED':
        return 'default';
      case 'REJECTED':
        return 'destructive';
      case 'UNDER_REVIEW':
        return 'outline';
      default:
        return 'outline';
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return moment(dateString).format('MMM DD, YYYY HH:mm');
  };

  if (!claim) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Claim Details</DialogTitle>
            <DialogDescription>Loading claim information...</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-10">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Claim for {claim.order.orderId}</span>
            <Badge variant={getStatusBadgeVariant(claim.status)}>{formatClaimStatusText(claim.status)}</Badge>
          </DialogTitle>
          <DialogDescription>Order Reference: {claim.order?.orderRef || 'N/A'}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Order Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                <span>Order Information</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => history.push(`/orders/${claim.order.orderId}`)}
                  className="h-8 w-8 p-0"
                >
                  <ArrowUpRight className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Order ID</Label>
                  <p className="text-sm">#{claim.order.orderId}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Order Reference</Label>
                  <p className="text-sm">{claim.order?.orderRef || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Order Value</Label>
                  <p className="text-sm">€ {claim.order?.orderValue?.toFixed(2) || '0.00'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Claim Value</Label>
                  <p className="text-sm">
                    € {claim.order?.orderValue > 40 ? '40.0' : claim.order?.orderValue?.toFixed(2)}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Company</Label>
                  <p className="text-sm">{claim.order?.company?.name || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Completed At</Label>
                  <p className="text-sm">
                    {claim.order?.completedAt ? formatDate(claim.order.completedAt) : 'Not completed'}
                  </p>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Delivery Address</Label>
                <p className="text-sm">{claim.order?.deliveryAddress || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Delivery Name</Label>
                <p className="text-sm">{claim.order?.deliveryName || 'N/A'}</p>
              </div>
            </CardContent>
          </Card>

          {/* Claim Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Claim Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Created</Label>
                  <p className="text-sm">{formatDate(claim.createdAt)}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Last Updated</Label>
                  <p className="text-sm">{formatDate(claim.updatedAt)}</p>
                </div>
                {claim.orderCompletedAt && (
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Specified Completed At</Label>
                    <p className="text-sm">{formatDate(claim.orderCompletedAt)}</p>
                  </div>
                )}
                {claim.resolvedAt && (
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Resolved</Label>
                    <p className="text-sm">{formatDate(claim.resolvedAt)}</p>
                  </div>
                )}
              </div>

              {/* Editable Fields */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="status" className="text-sm font-medium">
                    Status
                  </Label>
                  {isEditing && userRole === 'ADMIN' ? (
                    <Select value={editedStatus} onValueChange={setEditedStatus}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FILED">Filed</SelectItem>
                        <SelectItem value="GRANTED">Granted</SelectItem>
                        <SelectItem value="REJECTED">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="mt-1">
                      <Badge variant={getStatusBadgeVariant(claim.status)}>{claim.status}</Badge>
                    </div>
                  )}
                  {isEditing && userRole === 'CARRIER_USER' && claim.status === 'FILED' && (
                    <div className="mt-4 space-y-3">
                      <Label className="text-sm font-medium">Decision</Label>
                      <RadioGroup
                        value={carrierDecision || ''}
                        onValueChange={(value) => handleCarrierDecision(value as 'grant' | 'reject')}
                        className="flex space-x-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="grant" id="grant" className="hidden" />
                          <Label
                            htmlFor="grant"
                            className={`text-sm cursor-pointer px-3 py-2 rounded-md border transition-colors ${
                              carrierDecision === 'grant'
                                ? 'border-primary bg-primary/5 text-primary'
                                : 'border-border hover:border-primary/50'
                            }`}
                          >
                            Grant Claim
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="reject" id="reject" className="hidden" />
                          <Label
                            htmlFor="reject"
                            className={`text-sm cursor-pointer px-3 py-2 rounded-md border transition-colors ${
                              carrierDecision === 'reject'
                                ? 'border-primary bg-primary/5 text-primary'
                                : 'border-border hover:border-primary/50'
                            }`}
                          >
                            Reject Claim
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>
                  )}

                  {/* Completed At Date/Time Picker for Carrier Users */}
                  {isEditing &&
                    userRole === 'CARRIER_USER' &&
                    carrierDecision === 'reject' &&
                    !claim.order?.completedAt && (
                      <div className="mt-4 space-y-3">
                        <Label className="text-sm font-medium">Completion Date & Time</Label>
                        <div className="flex items-center space-x-2">
                          <Popover modal={true} open={calendarOpen} onOpenChange={setCalendarOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className={cn(
                                  'w-[240px] pl-3 text-left font-normal',
                                  !completedAtDate && 'text-muted-foreground',
                                )}
                              >
                                {completedAtDate ? (
                                  format(completedAtDate, 'PPP')
                                ) : (
                                  <span>Select completion date...</span>
                                )}
                                {!completedAtDate ? (
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                ) : (
                                  <div
                                    className="ml-auto h-4 w-4"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCompletedAtDate(null);
                                    }}
                                  >
                                    <CircleX className="opacity-50" />
                                  </div>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={completedAtDate}
                                onSelect={(date) => {
                                  setCompletedAtDate(date);
                                  setCalendarOpen(false);
                                }}
                              />
                            </PopoverContent>
                          </Popover>
                          <span className="text-sm">at</span>
                          <Input
                            type="time"
                            value={completedAtTime}
                            onChange={(e) => setCompletedAtTime(e.target.value)}
                            className="w-[120px]"
                            placeholder="Time"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          In case you believe the order was delivered, specify when the order was completed to support
                          your rejection.
                        </p>
                      </div>
                    )}
                </div>

                <div>
                  <Label htmlFor="reason" className="text-sm font-medium">
                    Reason
                  </Label>
                  {isEditing ? (
                    <Textarea
                      id="reason"
                      value={editedReason}
                      onChange={(e) => setEditedReason(e.target.value)}
                      placeholder="Enter claim reason..."
                      className="mt-1 min-h-[100px]"
                    />
                  ) : (
                    <p className="text-sm mt-1 text-muted-foreground">{claim.reason || 'No reason provided'}</p>
                  )}
                </div>
              </div>

              {/* Image Upload Section */}
              <div>
                <Label className="text-sm font-medium">Supporting Image</Label>
                {isEditing ? (
                  <div className="mt-2 space-y-3">
                    {/* Current Image Display */}
                    {claim.image && !preview && (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">Current Image</p>
                        <div className="relative inline-block">
                          <img
                            src={claim.image}
                            alt="Current claim image"
                            className="h-32 w-32 rounded-lg object-cover border cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => handleImageClick(claim.image)}
                          />
                          <Button
                            variant="destructive"
                            size="icon"
                            className="absolute -top-2 -right-2 h-6 w-6"
                            onClick={handleRemoveImage}
                            disabled={isRemovingImage}
                          >
                            {isRemovingImage ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* File Upload Area */}
                    <Card
                      className={`border-2 border-dashed transition-colors ${
                        isDragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
                      }`}
                    >
                      <CardContent className="p-4">
                        {!preview ? (
                          <div
                            className="flex flex-col items-center justify-center space-y-3"
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                          >
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                              <Upload className="h-5 w-5 text-muted-foreground" />
                            </div>
                            <div className="text-center">
                              <p className="text-sm font-medium">
                                Drag and drop an image here, or{' '}
                                <label className="cursor-pointer text-primary hover:text-primary/80">
                                  browse
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={handleFileInputChange}
                                  />
                                </label>
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">PNG, JPG, JPEG up to 5MB</p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center space-y-3">
                            <div className="relative">
                              <img
                                src={preview}
                                alt="Preview"
                                className="h-24 w-24 rounded-lg object-cover cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => handleImageClick(preview)}
                              />
                              <Button
                                variant="destructive"
                                size="icon"
                                className="absolute -top-2 -right-2 h-6 w-6"
                                onClick={handleRemoveImage}
                                disabled={isRemovingImage}
                              >
                                {isRemovingImage ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <X className="h-3 w-3" />
                                )}
                              </Button>
                            </div>
                            <p className="text-sm text-muted-foreground">{file?.name}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  <div className="mt-2">
                    {claim.image ? (
                      <div className="relative inline-block">
                        <img
                          src={claim.image}
                          alt="Claim image"
                          className="h-32 w-32 rounded-lg object-cover border cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => handleImageClick(claim.image)}
                        />
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No supporting image provided</p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="flex justify-between">
          {canEditClaim() && (
            <div className="flex space-x-2">
              {!isEditing ? (
                <Button variant="outline" onClick={() => setIsEditing(true)} disabled={isUpdating}>
                  Edit Claim
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={handleCancelEdit} disabled={isUpdating}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={isUpdating}>
                    {isUpdating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isUpdating}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Image Modal */}
      <Dialog open={isImageModalOpen} onOpenChange={setIsImageModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle>Claim Image</DialogTitle>
          </DialogHeader>
          <div className="p-6 pt-0">
            {selectedImage && (
              <div className="flex justify-center">
                <img
                  src={selectedImage}
                  alt="Claim image"
                  className="max-w-full max-h-[70vh] object-contain rounded-lg"
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default ClaimView;
